import type { Staff } from '../types';

/** A staff member shaped for a filter dropdown (Owner picker, owner chip). */
export interface StaffOption {
  /** Staff id, stringified — what the filters store and send to the API. */
  value: string;
  /** Display name. Never blank, so a row is always identifiable. */
  label: string;
  /** Comma-joined team names ("Name · Team" per Figma), absent when teamless. */
  team?: string;
  /**
   * What cmdk matches the typed query against. Always a non-empty string:
   * `Command.Item` reads `value` in a layout effect and does
   * `typeof v === 'object' && 'current' in v`, so a `null` here (staff records
   * do come back without a fullname) throws "Cannot use 'in' operator to
   * search for 'current' in null" and takes the whole page down.
   */
  searchValue: string;
}

const trimmed = (value?: string | null): string => (typeof value === 'string' ? value.trim() : '');

export const toStaffOption = (staff: Staff): StaffOption => {
  const label = trimmed(staff.fullname) || trimmed(staff.email) || `Staff #${staff.id}`;
  const team = staff.teams?.map((t) => t.team_name).join(', ') || undefined;

  return {
    value: String(staff.id),
    label,
    team,
    // Team is part of the search value so typing a team name narrows the list.
    searchValue: team ? `${label} ${team}` : label,
  };
};
